import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import FormikPhoneInput from 'shared/components/FormikPhoneInput/FormikPhoneInput'
import { StyledLink } from 'shared/components/Typography'
import { RegisterFormValues } from 'shared/models/user'
import { commonRouteConfig } from 'shared/routeConfig'
import { getPxAsRem } from 'shared/utils'
import { getSubdomain } from 'shared/utils/routing'
import * as Yup from 'yup'
import {
    StyledButtonContainer,
    StyledSubtext,
    StyledTextInput,
} from '../styled'

const initialFormValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
}

export interface SignUpFormProps {
    onSubmit: (
        values: RegisterFormValues,
        helpers: FormikHelpers<RegisterFormValues>
    ) => Promise<void | null>
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onSubmit }) => {
    const registerValidationScheme = Yup.object().shape({
        firstName: Yup.string().required('Required'),
        lastName: Yup.string().required('Required'),
        phone: Yup.string().required('Required'),
        email: Yup.string().email('Invalid email').required('Required'),
    })

    const envType = getSubdomain(process.env.REACT_APP_DOMAIN)

    return (
        <React.Fragment>
            <Formik
                initialValues={initialFormValues}
                onSubmit={onSubmit}
                validationSchema={registerValidationScheme}
                validateOnBlur
                style={{ width: '100%' }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form>
                        <Field name="firstName">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="firstName"
                                    size="small"
                                    error={
                                        !!errors.firstName && touched.firstName
                                    }
                                    helperText={
                                        !!errors.firstName &&
                                        touched.firstName &&
                                        errors.firstName
                                    }
                                    label="First Name"
                                    placeholder="First Name"
                                />
                            )}
                        </Field>
                        <Field name="lastName">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="lastName"
                                    size="small"
                                    error={
                                        !!errors.lastName && touched.lastName
                                    }
                                    helperText={
                                        !!errors.lastName &&
                                        touched.lastName &&
                                        errors.lastName
                                    }
                                    label="Last Name"
                                    placeholder="Last Name"
                                />
                            )}
                        </Field>
                        <Field name="phone">
                            {({ field }: FieldProps<string>) => (
                                <FormikPhoneInput
                                    {...field}
                                    id="phone"
                                    size="small"
                                    error={!!errors.phone && touched.phone}
                                    helperText={
                                        !!errors.phone && touched.phone
                                            ? errors.phone
                                            : 'Your personal cell is recommended'
                                    }
                                    label="Cell Phone Number"
                                    fieldName="phone"
                                />
                            )}
                        </Field>
                        <Field name="email">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    sx={{ marginTop: getPxAsRem(24) }}
                                    InputLabelProps={{ shrink: true }}
                                    id="email"
                                    size="small"
                                    error={!!errors.email && touched.email}
                                    helperText={
                                        !!errors.email && touched.email
                                            ? errors.email
                                            : envType === 'candidate'
                                              ? 'A personal email is recommended'
                                              : 'A work email is recommended'
                                    }
                                    label="Email"
                                    placeholder="Email"
                                />
                            )}
                        </Field>
                        <StyledButtonContainer>
                            <Button type="submit" disabled={isSubmitting}>
                                Sign Up
                            </Button>
                        </StyledButtonContainer>
                        <StyledSubtext>
                            <Typography variant="body2">
                                Already Have An Account?
                            </Typography>
                            <StyledLink
                                href={commonRouteConfig.login.path}
                                style={{ textDecoration: 'none' }}
                            >
                                Sign In
                            </StyledLink>
                        </StyledSubtext>
                    </Form>
                )}
            </Formik>
        </React.Fragment>
    )
}

export default SignUpForm
